import React, { memo, useCallback } from 'react';
import noop from 'lodash/noop';

import { Close } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Stack, Tooltip, Typography } from '@lyearn/ui';

import getTagIcon from '../../views/TagsManager/utils/getTagIcon';
import { actionButtonInFocus } from './styles';
import { ActionButtonProps } from './types';

const ActionButton = React.forwardRef(
  (
    {
      tag,
      onDelete,
      onClick,
      className,
      selectedTagValue,
      defaultPlaceholder,
      error,
      count,
      isInFocus,
      readOnly,
      tooltipText,
      disabledMessage,
    }: ActionButtonProps,
    ref?: React.Ref<HTMLButtonElement> | undefined,
  ) => {
    const _onDelete = useCallback(
      (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation();
        onDelete?.();
      },
      [onDelete],
    );
    const hasError = error && !selectedTagValue && tag.settings.requiredTag;
    const isDisabled = Boolean(disabledMessage);

    const Count = count ? (
      <Typography className="" variant="body-short-02">
        +{count}
      </Typography>
    ) : null;

    return (
      <Box
        //@ts-ignore
        ref={ref}
        className={cn(
          'flex h-40 max-w-full items-center justify-between truncate rounded-8 border px-12',
          className,
          { 'hover:bg-s-info-light': !readOnly && !isDisabled },
          hasError ? 'border-border-error-dark' : 'border-divider',
          isInFocus ? actionButtonInFocus : '',
          { 'pointer-events-none bg-bg-secondary opacity-50': isDisabled },
        )}
        role={!readOnly && !isDisabled && onClick ? 'button' : undefined}
        tabIndex={!isDisabled && onClick ? 0 : undefined}
        onClick={!readOnly && !isDisabled ? onClick : noop}>
        <Stack className="truncate">
          {getTagIcon(tag)}
          <Typography className="mx-8 truncate" color="text-text-secondary" variant="body-short-02">
            {tag.name}:
          </Typography>
          {selectedTagValue ? (
            <>
              <Tooltip placement="top" title={`${tooltipText || selectedTagValue}`}>
                <Typography className="truncate" variant="body-short-02">
                  {selectedTagValue}
                </Typography>
              </Tooltip>
              {Count}
            </>
          ) : (
            <Typography color="text-text-placeholder" variant="body-short-02">
              {defaultPlaceholder}
            </Typography>
          )}
        </Stack>
        {!readOnly && !isDisabled && onDelete ? (
          <LinkButton
            className="group flex-shrink-0 py-2 px-4 ps-0 pe-0 ms-8"
            color={'internal'}
            onClick={_onDelete}>
            <Close className="h-20 w-20 text-icon-placeholder group-hover:text-icon-primary" />
          </LinkButton>
        ) : null}
      </Box>
    );
  },
);

export default memo(ActionButton);
